import React, { type FC } from 'react';

import { IconButton, type IconProp } from '@atlaskit/button/new';
import AiGenerativeAudioBriefingIcon from '@atlaskit/icon-lab/core/ai-generative-audio-briefing';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Tooltip, { type PositionType } from '@atlaskit/tooltip';

import type { ActiveContent } from '../../../types/activeContent';
import { useAudioListenTrigger } from '../../hooks/useAudioListenTrigger';

type AudioListenButtonProps = {
	isDisabled?: boolean;
	isAutoOpenDisabled?: boolean;
	shape?: 'default' | 'circle';
	activeContent: ActiveContent;
	source: string;
	icon?: IconProp;
	tooltipPosition?: PositionType;
};

export const AudioListenButton: FC<AudioListenButtonProps> = ({
	shape = 'default',
	isDisabled,
	isAutoOpenDisabled = false,
	activeContent,
	source,
	icon,
	tooltipPosition = 'left',
}) => {
	const {
		handleClick,
		handleMouseEnter,
		handleMouseLeave,
		isAudioSupported,
		tooltipContent,
		isHovered,
		buttonRef,
		label,
	} = useAudioListenTrigger({
		activeContent,
		source,
		isDisabled,
		isAutoOpenDisabled,
	});

	return (
		<Tooltip content={tooltipContent} position={tooltipPosition}>
			<IconButton
				ref={buttonRef}
				label={label}
				appearance="subtle"
				icon={icon || (isHovered ? LinkExternalIcon : AiGenerativeAudioBriefingIcon)}
				shape={shape}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleClick}
				testId="audio-listen-button"
				isDisabled={!isAudioSupported}
			/>
		</Tooltip>
	);
};
